import Model, { encoders, ID, JsonArray, MODEL_LEVEL } from 'src/models/Model'
import Movie from 'src/models/movie/Movie'
import ShowingBadge from 'src/models/showing/ShowingBadge'

export default class MovieTerm extends Model {
  static name = 'MovieTerm'

  static deletable = true

  static duplicatable = true

  static versioned = true

  static level = MODEL_LEVEL.SITE

  static employeeRequiredPermissionCategories = [Model.permissionCategories.FilmProgramming]

  static define () {
    this.fields = {
      id: {
        type: ID,
        sortable: true
      },
      movie: {
        type: Movie,
        default: null,
        encode: encoders.hasOne,
        reference: true
      },
      showingBadges: {
        type: [ShowingBadge],
        default: () => ([]),
        encode (data) {
          data.showingBadgeIds = _.map(this.showingBadges.data, 'id')
        },
        reference: true
      },
      hasFlatFee: {
        type: Boolean,
        default: false
      },
      flatFee: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      minimumGuarantee: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      deposit: {
        type: Number,
        encode: encoders.cents,
        default: 0
      },
      previewTerms: {
        type: JsonArray,
        mergeDefault: true,
        default: () => ([])
      },
      weeklyTerms: {
        type: JsonArray,
        mergeDefault: true,
        default: () => ([0])
      },
      priorityOrder: {
        type: Number,
        default: 100
      },
      locked: {
        type: Boolean,
        default: false,
        excludeFromDefault: true,
        output: true
      },
      originalPriorityOrder: {
        type: Number,
        default: null,
        local: true
      },
      originalDescription: {
        type: String,
        default: null,
        local: true
      },
      libraryParentId: {
        type: ID,
        output: true,
        excludeFromDefault: true
      },
      receiveLibraryUpdates: {
        type: Boolean,
        excludeFromDefault: true
      },
      libraryChildSiteIds: {
        type: [ID],
        output: true,
        excludeFromDefault: true
      },
      libraryDescendantSites: {
        type: JsonArray,
        output: true,
        excludeFromDefault: true
      }
    }

    this.computed = {
      name () {
        if (this.showingBadges.data.length > 0) {
          return `${this.movie?.name} (${_.map(this.showingBadges.data, 'displayName').join(', ')})`
        } else {
          return this.movie?.name
        }
      },
      description () {
        if (this.showingBadges.data.length > 0) {
          return `${_.map(this.showingBadges.data, 'displayName').join(', ')}`
        } else {
          return 'Default'
        }
      },
      unreachableMovieTerm () {
        if (this.movie.movieTerms.data.length === 1) {
          return null
        }

        for (let index = 0; index < this.movie.movieTerms.data.length; index++) {
          const priorMovieTerm = this.movie.movieTerms.data[index]
          if ((index + 1) === this.priorityOrder) {
            return null
          }

          const priorMovieTermBadgeIds = Object.values(priorMovieTerm.showingBadgeIds || [])
          const currentMovieTermShowingBadgeIds = Object.values(this.showingBadgeIds || [])

          // If the current movie term's showing badges are a subset of the
          // prior movie term showing badges, then the current movie term will never be reached
          if (priorMovieTermBadgeIds.every(item => currentMovieTermShowingBadgeIds.includes(item))) {
            return priorMovieTerm
          }
        }
        return null
      },
      tooltip () {
        if (this.unreachableMovieTerm) {
          return `This movie term will always be unreachable because movie term with Priority
           ${this.unreachableMovieTerm.priorityOrder} matches first for any showings with showing
           badges ${_.map(_.intersection(this.showingBadges.data, this.unreachableMovieTerm.showingBadges.data),
            'displayName').join(', ')}`
        }

        const excludedShowingBadgeNames = this.movie.movieTerms.data
          .slice(0, this.priorityOrder - 1)
          .map(term => `(${term.showingBadges.data.map(badge => badge.displayName).join(', ')})`)
          .join(', ')

        if (this.showingBadges.data.length === 0) {
          return `This movie term applies to all showings${excludedShowingBadgeNames ? ` without ${excludedShowingBadgeNames}.` : '.'}`
        }

        const movieTermShowingBadgeNames = this.showingBadges.data.map(badge => badge.displayName)
        const includedShowingBadgeNames = movieTermShowingBadgeNames.length > 1
          ? movieTermShowingBadgeNames.slice(0, -1).join(', ') + ' and ' + movieTermShowingBadgeNames.slice(-1)
          : movieTermShowingBadgeNames[0]

        const includedText = includedShowingBadgeNames ? ` with ${includedShowingBadgeNames}` : ''
        const excludedText = excludedShowingBadgeNames ? ` without ${excludedShowingBadgeNames}` : ''

        return `This movie term applies to showings${includedText}${excludedText}.`
      },
      numberOfShowingBadges () {
        return this.showingBadges.data.length
      },
      numberOfPriorityShowingBadges () {
        return _.countBy(this.showingBadges.data, 'conferPriority').true || 0
      },
      totalShowingBadgeDisplayOrder () {
        return _.sumBy(this.showingBadges.data, 'displayOrder')
      },
      allWeeklyTerms () {
        const allWeeklyTerms = []

        _.each(this.previewTerms, (previewTerm, index) => {
          const isLastWeek = (index === (this.previewTerms.length - 1))

          allWeeklyTerms.push({
            isFirstWeek: index === 0,
            isPreview: true,
            index,
            week: `Preview Week ${index + 1}${isLastWeek ? '+' : ''}`,
            term: previewTerm
          })
        })

        _.each(this.weeklyTerms, (weeklyTerm, index) => {
          const isLastWeek = (index === (this.weeklyTerms.length - 1))

          allWeeklyTerms.push({
            isFirstWeek: index === 0,
            isPreview: false,
            index,
            week: `Week ${index + 1}${isLastWeek ? '+' : ''}`,
            term: weeklyTerm
          })
        })

        return allWeeklyTerms
      }
    }
  }
}
